import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LogIn, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Global dialog that appears when an auth error (401/403/session expired) is detected.
 * Listens to a custom event dispatched by the fetch interceptor.
 */
export function SessionExpiredDialog() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleAuthError = useCallback(() => {
    // Only show if we're not already on the auth page
    if (window.location.pathname === "/auth") return;
    setOpen(true);
  }, []);

  useEffect(() => {
    // Listen for custom auth-error events
    window.addEventListener("supabase:auth-error", handleAuthError);

    // Listen for Supabase auth state changes (e.g. token refresh failure)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT" || event === "TOKEN_REFRESHED") {
        if (event === "SIGNED_OUT") {
          handleAuthError();
        }
      }
    });

    return () => {
      window.removeEventListener("supabase:auth-error", handleAuthError);
      subscription.unsubscribe();
    };
  }, [handleAuthError]);

  const handleLogin = () => {
    setOpen(false);
    navigate("/auth", { replace: true });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            Session Expired
          </DialogTitle>
          <DialogDescription>
            Your session has expired or you need to sign in to continue. Please log in again.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Dismiss
          </Button>
          <Button onClick={handleLogin} className="gap-1.5">
            <LogIn className="w-4 h-4" />
            Sign In
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
